import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../lib/api';
import { Breadcrumbs, BreadcrumbItem, LoadingState } from '../components/ui';
import { alert } from '../utils/toast';
import type { AlertConfig } from '../types';
import {
  BellRing,
  Plus,
  Activity,
  Gauge,
  Clock,
  AlertTriangle,
  Users,
  Edit2,
  Trash2,
  X,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';

export const AlertConfiguration: React.FC = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState<AlertConfig | null>(null);
  const [formData, setFormData] = useState({
    metric_name: '',
    threshold: 0,
    comparison: 'greater_than' as 'greater_than' | 'less_than' | 'equals',
    notification_emails: '',
    enabled: true,
  });

  // Fechar modal com tecla Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const { data: alerts, isLoading } = useQuery({
    queryKey: ['alert-configs'],
    queryFn: async () => {
      try {
        const res = await api.get<any[]>('/admin/alerts');
        const list = Array.isArray(res.data) ? res.data : (res.data as any)?.data || [];
        return list.map((item: any) => ({
          id: item.id,
          metric_name: item.metric_name || item.metric || 'error_rate',
          threshold: Number(item.threshold ?? 0),
          comparison:
            item.comparison ||
            (item.condition === 'lt'
              ? 'less_than'
              : item.condition === 'eq'
                ? 'equals'
                : 'greater_than'),
          notification_emails: Array.isArray(item.notification_emails)
            ? item.notification_emails
            : Array.isArray(item.recipients)
              ? item.recipients
              : ['devops@tatusolutions.com'],
          enabled: item.enabled ?? true,
        })) as AlertConfig[];
      } catch {
        return [] as AlertConfig[];
      }
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await api.post('/admin/alerts', {
        ...data,
        notification_emails: data.notification_emails.split(',').map((e) => e.trim()),
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-configs'] });
      closeModal();
      void alert.success('Criado!', 'Alerta criado com sucesso!');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { id: string } & typeof formData) => {
      const { id, ...updateData } = data;
      const response = await api.put(`/admin/alerts/${id}`, {
        ...updateData,
        notification_emails: updateData.notification_emails.split(',').map((e) => e.trim()),
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-configs'] });
      closeModal();
      void alert.success('Atualizado!', 'Alerta atualizado com sucesso!');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/alerts/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-configs'] });
      void alert.success('Eliminado!', 'Alerta eliminado com sucesso!');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingAlert) {
      updateMutation.mutate({ id: editingAlert.id, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (alertItem: AlertConfig) => {
    setEditingAlert(alertItem);
    setFormData({
      metric_name: alertItem.metric_name,
      threshold: alertItem.threshold,
      comparison: alertItem.comparison,
      notification_emails: alertItem.notification_emails.join(', '),
      enabled: alertItem.enabled,
    });
    setShowCreateModal(true);
  };

  const handleDelete = async (alertObj: AlertConfig) => {
    const result = await alert.confirm(
      'Eliminar Alerta?',
      `Tem a certeza de que deseja eliminar o alerta configurado para "${getMetricLabelPT(alertObj.metric_name)}"?`
    );

    if (result.isConfirmed) {
      deleteMutation.mutate(alertObj.id);
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingAlert(null);
    setFormData({
      metric_name: '',
      threshold: 0,
      comparison: 'greater_than',
      notification_emails: '',
      enabled: true,
    });
  };

  const getMetricLabelPT = (metric: string) => {
    const metrics: Record<string, string> = {
      total_requests: 'Total de Pedidos',
      error_rate: 'Taxa de Erro (%)',
      average_response_time: 'Tempo Médio de Resposta (ms)',
      failed_requests: 'Pedidos Falhados',
      active_clients: 'Clientes Ativos',
    };
    return metrics[metric] || metric;
  };

  const getMetricIcon = (metric: string) => {
    switch (metric) {
      case 'total_requests':
        return <Activity className="w-4 h-4 text-blue-500" />;
      case 'error_rate':
      case 'failed_requests':
        return <AlertTriangle className="w-4 h-4 text-rose-500" />;
      case 'average_response_time':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'active_clients':
        return <Users className="w-4 h-4 text-emerald-500" />;
      default:
        return <Gauge className="w-4 h-4 text-primary-500" />;
    }
  };

  const getComparisonLabelPT = (comparison: string) => {
    const comparisons: Record<string, string> = {
      greater_than: '> Maior que',
      less_than: '< Menor que',
      equals: '= Igual a',
    };
    return comparisons[comparison] || comparison;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem>Configuração de Alertas</BreadcrumbItem>
        </Breadcrumbs>
        <LoadingState type="card" count={2} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Configuração de Alertas</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white flex items-center gap-3">
            <BellRing className="w-8 h-8 text-primary-600 dark:text-primary-400" /> Configuração de
            Alertas
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
            Configure limites de métricas e receba notificações automáticas de incidentes
            operacionais.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2 shadow-sm hover:shadow"
        >
          <Plus className="w-4 h-4" /> Criar Alerta
        </button>
      </div>

      {alerts && alerts.length > 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 text-xs uppercase font-bold text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-6 py-4">Métrica</th>
                  <th className="px-6 py-4">Condição</th>
                  <th className="px-6 py-4">Limite</th>
                  <th className="px-6 py-4">Emails de Notificação</th>
                  <th className="px-6 py-4">Estado</th>
                  <th className="px-6 py-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {alerts.map((alertItem) => (
                  <tr
                    key={alertItem.id}
                    className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors"
                  >
                    <td className="px-6 py-4 font-semibold text-slate-900 dark:text-white flex items-center gap-2.5">
                      {getMetricIcon(alertItem.metric_name)}
                      {getMetricLabelPT(alertItem.metric_name)}
                    </td>
                    <td className="px-6 py-4 text-slate-700 dark:text-slate-300 font-medium">
                      <span className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-semibold text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700">
                        {getComparisonLabelPT(alertItem.comparison)}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-mono font-bold text-slate-900 dark:text-white">
                      {alertItem.threshold}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-600 dark:text-slate-400">
                      <div className="flex flex-wrap gap-1">
                        {alertItem.notification_emails.map((email, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
                          >
                            {email}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                          alertItem.enabled
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        {alertItem.enabled ? (
                          <>
                            <CheckCircle2 className="w-3.5 h-3.5" /> Ativo
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-3.5 h-3.5" /> Inativo
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(alertItem)}
                          className="p-1.5 rounded-lg text-slate-500 hover:text-primary-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(alertItem)}
                          className="p-1.5 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-12 text-center shadow-sm">
          <div className="w-16 h-16 rounded-2xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center mx-auto mb-4">
            <BellRing className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
            Nenhum alerta configurado
          </h3>
          <p className="text-slate-600 dark:text-slate-400 text-sm max-w-md mx-auto mb-6">
            Crie o seu primeiro alerta de métricas para monitorizar o desempenho da plataforma e
            receber notificações automáticas por email.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary inline-flex items-center gap-2 shadow-md hover:shadow-lg"
          >
            <Plus className="w-4 h-4" /> Criar o Seu Primeiro Alerta
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 max-w-xl w-full shadow-2xl border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800 mb-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
                <BellRing className="w-5 h-5 text-primary-500" />
                {editingAlert ? 'Editar Regra de Alerta' : 'Criar Nova Regra de Alerta'}
              </h3>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {(createMutation.isError || updateMutation.isError) && (
                <div className="rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 p-4">
                  <p className="text-sm font-medium text-rose-800 dark:text-rose-300">
                    {getErrorMessage(createMutation.error || updateMutation.error)}
                  </p>
                </div>
              )}

              <div>
                <label
                  htmlFor="metric_name"
                  className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5"
                >
                  Nome da Métrica *
                </label>
                <select
                  id="metric_name"
                  required
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-colors"
                  value={formData.metric_name}
                  onChange={(e) => setFormData({ ...formData, metric_name: e.target.value })}
                >
                  <option value="">Selecione uma métrica...</option>
                  <option value="total_requests">Total de Pedidos</option>
                  <option value="error_rate">Taxa de Erro (%)</option>
                  <option value="average_response_time">Tempo Médio de Resposta (ms)</option>
                  <option value="failed_requests">Pedidos Falhados</option>
                  <option value="active_clients">Clientes Ativos</option>
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="comparison"
                    className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5"
                  >
                    Condição *
                  </label>
                  <select
                    id="comparison"
                    required
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-colors"
                    value={formData.comparison}
                    onChange={(e) =>
                      setFormData({ ...formData, comparison: e.target.value as any })
                    }
                  >
                    <option value="greater_than">Maior Que (&gt;)</option>
                    <option value="less_than">Menor Que (&lt;)</option>
                    <option value="equals">Igual A (=)</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="threshold"
                    className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5"
                  >
                    Limite / Threshold *
                  </label>
                  <input
                    id="threshold"
                    type="number"
                    required
                    step="0.01"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-colors"
                    value={formData.threshold}
                    onChange={(e) =>
                      setFormData({ ...formData, threshold: parseFloat(e.target.value) })
                    }
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="notification_emails"
                  className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5"
                >
                  Emails de Notificação (separados por vírgula) *
                </label>
                <input
                  id="notification_emails"
                  type="text"
                  required
                  placeholder="admin@empresa.com, ops@empresa.com"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 outline-none transition-colors"
                  value={formData.notification_emails}
                  onChange={(e) =>
                    setFormData({ ...formData, notification_emails: e.target.value })
                  }
                />
              </div>

              <div className="pt-1">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 dark:border-slate-700 text-primary-600 focus:ring-primary-500"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Alerta ativo e em monitorização contínua
                  </span>
                </label>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-2.5 px-4 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold text-sm transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex-1 btn-primary py-2.5 text-sm font-semibold shadow-md"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'A guardar...'
                    : editingAlert
                      ? 'Atualizar Alerta'
                      : 'Guardar Alerta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
